import { useNovel } from '@/context/novel-context';
import { Note, NoteSummary } from '@common/schemas/notes.schema';
import { DocumentData, FieldValue, UpdateData } from 'firebase/firestore';
import { useCallback, useMemo } from 'react';
import { useNovelRepository } from './useNovelRepository';

export function useNotes() {
  const { novel } = useNovel();
  const { getNewId, notesDao, getSummaries } = useNovelRepository();

  const notes = getSummaries<NoteSummary>('notes');

  const createNote = useCallback(async (noteTitle: string, folderId?: string, predefinedId?: string, predefinedContentId?: string): Promise<Note> => {
    if (!novel.id || !notesDao) throw new Error('Novel not loaded');

    const newId = predefinedId || getNewId();
    const newNote: Note = {
      id: newId,
      title: noteTitle,
      contentId: predefinedContentId || getNewId(), // Need a contentId for the actual content
      folderId: folderId || null,
      tags: [],
      linkedEntityIds: [],
      isFavorite: false,
      audit: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    };

    await notesDao.setNoteItem(novel.ownerId, novel, newNote);
    return newNote;
  }, [novel, notesDao, getNewId]);

  const updateNote = useCallback(async (noteId: string, updates: UpdateData<DocumentData> | FieldValue) => {
    if (!novel.id || !notesDao) throw new Error('Novel not loaded');
    await notesDao.updateNoteItem(novel.ownerId, novel, noteId, updates);
  }, [novel, notesDao]);

  const deleteNote = useCallback(async (noteId: string) => {
    if (!novel.id || !notesDao) throw new Error('Novel not loaded');
    await notesDao.deleteNoteItem(novel.ownerId, novel, noteId);
  }, [novel, notesDao]);

  return useMemo(() => ({
    notes,
    createNote,
    updateNote,
    deleteNote,
  }), [notes, createNote, updateNote, deleteNote]);
}
